import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from "firebase/firestore";
import {
  GetManyReferenceParams,
  GetManyReferenceResult,
  GetOneParams,
  GetOneResult,
  UpdateParams,
  UpdateResult,
} from "react-admin";
import { applyFilter, applyPagination, applySort } from "../backoffice.utils";
import { firestore } from "../firebase";
import { ASFCourseSession, TheoryExam, TheoryLesson } from "../model/autovioCalendarEvents";
import { theoryLessonConverter } from "./converter/theoryLessonConverter";
import { theoryExamConverter } from "./converter/theoryExamConverter";
import { autovioCalendarEventConverter } from "./converter/autovioCalendarEventConverter";
import { commonCalendarEventQueryConstraints } from "../utils/firestore-queries";
import { AbstractDataProvider } from "./AbstractDataProvider";
import { EditCalendarEventDto, getAuthenticatedServerClient, schemas } from "../api/server.api";
import { updateApplied } from "../utils/updateApplied";
import { diff as deepObjectDiff } from "deep-object-diff";
import { theoryLectureFromAbbreviation } from "../model/TheoryLecture";

const EditTheoryLessonDtoSchema = schemas.EditCalendarEventDto3.options.find(
  (it) => it.shape.type._def.value === "TheoryLesson",
)!;

const _theoryEventTypes = new Set(["TheoryLesson", "TheoryExam", "ASFCourseSession"]);

export type TheoryEvent = TheoryLesson | TheoryExam | ASFCourseSession;

class TheoryEventsProvider extends AbstractDataProvider<TheoryEvent> {
  async getOne(_: string, { id }: GetOneParams): Promise<GetOneResult<TheoryEvent>> {
    const snapshot = await getDoc(doc(firestore, `calendar_events/${id}`));
    const event = autovioCalendarEventConverter.fromFirestore(snapshot);
    if (event.type === "TheoryLesson" || event.type === "TheoryExam" || event.type === "ASFCourseSession") {
      if (event.type === "ASFCourseSession" && event.sessionType === "fahrprobe") {
        throw new Error(`ASFCourseSession ${id} has sessionType: "fahrprobe" -- expected: "sitzung1", "sitzung2", ...`);
      }
      return { data: event };
    }
    throw new Error(
      `Calendar event ${id} is of type: ${event.type} -- expected: TheoryLesson, TheoryExam, or ASFCourseSession`,
    );
  }

  async getManyReference(
    resource: string,
    { target, id, filter, sort, pagination }: GetManyReferenceParams,
  ): Promise<GetManyReferenceResult<TheoryEvent>> {
    const { from, to, types, ...restFilters } = filter;
    if (types) {
      if (!Array.isArray(types) || !types.every((it) => _theoryEventTypes.has(it.type))) {
        throw new Error(`Invalid filter.types: ${JSON.stringify(types)}`);
      }
    }
    const fetchTypes: Set<"TheoryLesson" | "TheoryExam" | "ASFCourseSession"> = types
      ? new Set<"TheoryLesson" | "TheoryExam" | "ASFCourseSession">(types)
      : resource === "theoryLessons"
        ? new Set(["TheoryLesson"] as const)
        : new Set(["TheoryLesson", "TheoryExam", "ASFCourseSession"] as const);

    let theoryLessons: TheoryLesson[] = [];
    if (fetchTypes.has("TheoryLesson")) {
      let q = query(
        collection(firestore, `/calendar_events`),
        where("type", "==", "TheoryLesson"),
        where(`deleted`, "==", false),
      );
      q = commonCalendarEventQueryConstraints(q, { target, id, from, to });
      const snapshot1 = await getDocs(q);
      theoryLessons = snapshot1.docs.map(theoryLessonConverter.fromFirestore);
    }

    let theoryExams: TheoryExam[] = [];
    if (fetchTypes.has("TheoryExam")) {
      let q = query(
        collection(firestore, `/calendar_events`),
        where("drivingLessonType", "==", "theoretischePruefung"),
        where(`deleted`, "==", false),
      );
      q = commonCalendarEventQueryConstraints(q, { target, id, from, to });
      const snapshot2 = await getDocs(q);
      theoryExams = snapshot2.docs.map(theoryExamConverter.fromFirestore);
    }

    let asfCourseSessions: ASFCourseSession[] = [];
    if (fetchTypes.has("ASFCourseSession")) {
      let q = query(
        collection(firestore, `/calendar_events`),
        where("type", "==", "ASFCourseSession"),
        where(`deleted`, "==", false),
      );
      q = commonCalendarEventQueryConstraints(q, { target, id, from, to });
      const snapshot3 = await getDocs(q);
      asfCourseSessions = (
        snapshot3.docs.map(autovioCalendarEventConverter.fromFirestore) as ASFCourseSession[]
      ).filter((it) => it.sessionType !== "fahrprobe");
    }

    let targetString = "";
    switch (target) {
      case "derived.studentUids":
      case "studentUid":
      case "studentUids":
        targetString = "student";
        break;
      case "drivingSchoolUid":
        targetString = "driving school";
        break;
      default:
        throw new Error(`Unexpected target: ${JSON.stringify(target)}`);
    }
    console.info(`Retrieved ${theoryLessons.length} theory lesson(s) for ${targetString} ${id}`);
    console.info(`Retrieved ${theoryExams.length} theory exam(s) for ${targetString} ${id}`);
    console.info(`Retrieved ${asfCourseSessions.length} ASF course session(s) for ${targetString} ${id}`);
    const data = (theoryLessons as Array<TheoryEvent>).concat(theoryExams).concat(asfCourseSessions);
    return applyPagination(applySort(applyFilter(data, restFilters), sort), pagination);
  }

  async update(_: string, { id, data, previousData }: UpdateParams): Promise<UpdateResult<TheoryEvent>> {
    const diff = deepObjectDiff(previousData, data) as Partial<TheoryEvent>;
    const updateData: Partial<TheoryEvent> = {};
    const dto: EditCalendarEventDto = { type: "TheoryLesson" };
    const unsupportedKeys: Array<string> = [];
    for (const key of Object.keys(diff)) {
      if (key === "theoryUnit") {
        unsupportedKeys.push(key);
      } else if (key === "theoryLectureAbbreviation") {
        const theoryLecture = theoryLectureFromAbbreviation(data[key]);
        const end = data.start.plus({ minutes: theoryLecture.category === "B96" ? 150 : 90 });
        Object.assign(updateData, { theoryLecture, end });
        Object.assign(dto, { theoryLecture, end: end.toISO() });
      } else if (key in EditTheoryLessonDtoSchema.shape) {
        const value = data[key];
        if (key === "start") {
          const start = value;
          const end = start.plus({ minutes: data.theoryLectureAbbreviation === "B96" ? 150 : 90 });
          Object.assign(updateData, { start, end });
          Object.assign(dto, { start: start.toISO(), end: end.toISO() });
        } else {
          updateData[key] = value;
          dto[key as keyof EditCalendarEventDto] = value;
        }
      } else {
        unsupportedKeys.push(key);
      }
    }
    if (unsupportedKeys.length === 1) {
      throw new Error(`Changing property ${unsupportedKeys[0]} is not supported.`);
    } else if (unsupportedKeys.length > 1) {
      throw new Error(`Changing properties ${unsupportedKeys.join(", ")} is not supported.`);
    }
    if (Object.keys(updateData).length > 0) {
      const serverClient = await getAuthenticatedServerClient();
      const updateReceived = new Promise<void>((resolve) => {
        const unsubscribe = onSnapshot(doc(firestore, `calendar_events/${id}`), (snapshot) => {
          const updatedTheoryLesson = theoryLessonConverter.fromFirestore(snapshot);
          if (updateApplied(updateData, updatedTheoryLesson)) {
            unsubscribe();
            resolve();
          }
        });
      });
      await serverClient.editEvent_v3(dto, { params: { eventId: id } });
      await updateReceived;
    }
    return this.getOne("", { id });
  }
}

export const theoryEventsProvider = new TheoryEventsProvider();
